// ============================================
// useTrading — Position management, order execution, limit orders
// Supports: Solana (Jupiter) + Hyperliquid futures
// ============================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  executeBuy, executeSell, executeLimitBuy, executeLimitSell,
  executeOCO, startOCOMonitor, cancelTriggerOrder,
  getTokenBalance, getEntryPrice, getOpenTriggerOrders,
  startPreWarm, stopPreWarm, setMarketSlippageBps,
  getTriggerOrderHistory, classifyTriggerOrder,
} from '../../utils/jupiter'
import {
  meteoraBuy, meteoraSell, placeMeteoraLimit, cancelMeteoraLimit,
  getMeteoraLimitStatus, listMeteoraLimitOrders,
} from '../../utils/meteoraTrade'
import { formatMcap } from '../../utils/chartUtils'
import { positionStore } from './positionStore'
import { isPaperActive, paperBuy, paperSell, paperPosition } from '../../utils/paperTrade'   // paperwallet-20261027
import * as positionSync from './positionSync'
import { dbg } from '../../utils/debugFlag'

// ─── Helius parsed transaction → precise entry ──────────────────────
// Goes through the auth-gated Pages Function proxy (/api/helius-v0/*),
// which injects the server-side HELIUS_API_KEY. Relative URL = same
// origin, session cookie sent automatically. The function prefixes /v0/
// upstream, so the path here must not include it.

// positions-20261017: the parsed swap becomes a FILL in the position's fill
// list (deduped by signature). It used to overwrite size + entry with just
// this one tx, which wiped the earlier buys on a top-up.
async function refineEntryFromTx(txid, token) {
  if (!txid) return
  try {
    // Give Helius time to index the tx
    await new Promise(r => setTimeout(r, 5000))

    const resp = await fetch(`/api/helius-v0/transactions/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactions: [txid] }),
    })
    if (!resp.ok) { console.warn(`[Helius] ${resp.status}`); return }

    const [parsed] = await resp.json()
    if (!parsed?.events?.swap) { console.warn('[Helius] No swap event in tx'); return }

    const swap = parsed.events.swap
    // Some Jupiter routes wrap SOL — fall back to inner wSOL transfers if nativeInput is empty
    let solSpent = (swap.nativeInput?.amount || 0) / 1e9
    if (solSpent <= 0 && Array.isArray(swap.tokenInputs)) {
      const wsolIn = swap.tokenInputs.find(t => t.mint === 'So11111111111111111111111111111111111111112')
      if (wsolIn) {
        const raw = wsolIn.rawTokenAmount
        solSpent = Number(raw.tokenAmount) / (10 ** raw.decimals)
      }
    }
    const tokenOut = swap.tokenOutputs?.find(t => t.mint === token)
    if (!tokenOut || solSpent <= 0) return

    const raw = tokenOut.rawTokenAmount
    const tokensReceived = Number(raw.tokenAmount) / (10 ** raw.decimals)
    if (!(tokensReceived > 0)) return

    // Sanity check — decimals/routing bugs in Helius parsed swaps typically
    // produce 10x+ deviations from the price we already have.
    const cur = positionStore.get(token)[0]
    const curPs = Number(cur?.entryPriceSol) || 0
    const ps = solSpent / tokensReceived
    if (curPs > 0 && (ps / curPs < 0.2 || ps / curPs > 5)) {
      console.warn(`[Helius] Fill price ${ps} is ${(ps / curPs).toFixed(2)}x current entry — skipping (likely decimals/routing bug)`)
      return
    }
    positionSync.addFill(token, { sig: txid, side: 'buy', tok: tokensReceived, sol: solSpent })
    dbg(`[Helius] Fill added: sol=${solSpent.toFixed(4)}, tokens=${tokensReceived.toFixed(2)}`)
  } catch (err) {
    console.warn('[Helius] refineEntryFromTx failed:', err)
  }
}

// ─── Hyperliquid WS request helper ───────────────────────────────────

function hlRequest(ws, action, params = {}, timeout = 15000) {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return reject(new Error('WebSocket not connected'))
    }
    // paperaudit-20261027: no futures order/close/leverage goes out while Paper is active.
    if (isPaperActive() && (action === 'hl_place_order' || action === 'hl_flash_close' || action === 'hl_set_leverage')) {
      return reject(new Error(PAPER_BLOCK))
    }
    const expectedTypes = {
      hl_place_order:   ['hl_order_result'],
      hl_cancel_order:  ['hl_order_result'],
      hl_flash_close:   ['hl_order_result'],
      hl_positions:     ['hl_positions'],
      hl_open_orders:   ['hl_open_orders'],
      hl_account:       ['hl_account'],
      hl_set_leverage:  ['hl_order_result'],
      hl_auth:          ['hl_auth_ack'],
      hl_poll:          ['hl_poll'],
      hl_fills:         ['hl_fills'],
    }
    const expected = expectedTypes[action] || ['hl_order_result', 'hl_error']
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (expected.includes(msg.type) || msg.type === 'hl_error') {
          cleanup()
          resolve(msg)
        }
      } catch {}
    }
    const timer = setTimeout(() => { cleanup(); reject(new Error(`Timeout: ${action}`)) }, timeout)
    const cleanup = () => { ws.removeEventListener('message', handler); clearTimeout(timer) }
    ws.addEventListener('message', handler)
    ws.send(JSON.stringify({ action, ...params }))
  })
}

// ─── HL leverage cache ───────────────────────────────────────────────
// HL leverage is a persistent per-coin account setting. Re-sending it before
// every order costs a full round-trip for nothing when it has not changed —
// which roughly doubles time-to-fill. Cache what we set and skip no-op calls.
const _hlLeverageCache = {}
async function ensureLeverage(ws, symbol, leverage) {
  if (_hlLeverageCache[symbol] === leverage) return
  try {
    await hlRequest(ws, 'hl_set_leverage', { symbol, leverage }, 5000)
    _hlLeverageCache[symbol] = leverage
  } catch (e) {
    console.warn('[HL] Set leverage failed:', e.message)
    // leave cache unset so the next order retries
  }
}

// Build a provisional fill from an hl_place_order response so the fills list
// updates the instant the order returns, instead of waiting on a re-poll. The
// authoritative fill (fee/PnL/tid) replaces this by oid when it arrives.
function hlFillFromStatuses(statuses, ctx) {
  if (!Array.isArray(statuses)) return null
  const filled = statuses.filter(s => s && s.filled)
  if (!filled.length) return null
  let totalSz = 0, notional = 0, oid = ''
  for (const s of filled) {
    const sz = parseFloat(s.filled.totalSz) || 0
    const px = parseFloat(s.filled.avgPx) || 0
    totalSz += sz; notional += sz * px
    if (s.filled.oid != null) oid = String(s.filled.oid)
  }
  if (totalSz <= 0) return null
  return {
    tid: `prov-${oid}-${Date.now()}`, oid, hash: '',
    symbol: ctx.symbol, side: ctx.side, dir: ctx.dir,
    qty: totalSz, price: notional / totalSz, notional,
    fee: 0, feeToken: 'USDC', closedPnl: 0,
    time: Date.now(), crossed: true, startPosition: '0', _provisional: true,
  }
}

// ═════════════════════════════════════════════════════════════════════

// ─── Order venue (orders-venue-20261016) ───────────────────────────────
// defaults.orderVenue: 'jupiter' (default) | 'meteora'. Applies to Solana
// market AND limit orders. Meteora never silently falls back to Jupiter —
// its errors reach the toast as-is.
const venueOf = (defaults) => (defaults?.orderVenue === 'meteora' ? 'meteora' : 'jupiter')
// paperwallet-20261027: the send point. Nothing real goes out while Paper is active.
const PAPER_BLOCK = 'Paper wallet active — nothing is sent on-chain'
// papernofee-20261027: paper fill = last print on that side (buy → offer, sell →
// bid) from the chart's trade buffer; no print on that side yet → last trade.
function paperFillMcap(rawTradesRef, lastBucketMcapRef, side) {
  const m = rawTradesRef?.current?.lastMcapOf?.(side)
  return m > 0 ? m : (lastBucketMcapRef?.current || 0)
}
function mktBuy(venue, tok, amount, pk, sign, slippageBps, onSent) {
  if (isPaperActive()) return Promise.reject(new Error(PAPER_BLOCK))
  return venue === 'meteora'
    ? meteoraBuy(tok, amount, pk, sign, slippageBps, onSent)
    : executeBuy(tok, amount, pk, sign, onSent)
}
function mktSell(venue, tok, pct, pk, sign, slippageBps, onSent) {
  if (isPaperActive()) return Promise.reject(new Error(PAPER_BLOCK))
  return venue === 'meteora'
    ? meteoraSell(tok, pct, pk, sign, slippageBps, onSent)
    : executeSell(tok, pct, pk, sign, onSent)
}

export default function useTrading({
  publicKey, signTransaction, currentToken, defaults, showToast,
  solPriceRef, lastBucketMcapRef, tokenSupplyRef, rawTradesRef,
  // ─── Futures (optional) ───
  dataSource = 'solana',
  wsRef = null,
  hlPair = null,
}) {
  const [positions, setPositions] = useState([])
  const [limitOrders, setLimitOrders] = useState([])
  const [tokenBalance, setTokenBalance] = useState(0)
  const [orderModal, setOrderModal] = useState({ show: false, side: 'buy', mcap: 0 })

  const positionsRef = useRef(positions)
  const limitOrdersRef = useRef(limitOrders)
  const dataSourceRef = useRef(dataSource)
  const hlPairRef = useRef(hlPair)
  const currentTokenRef = useRef(currentToken)
  const pollRef = useRef(null)

  useEffect(() => { positionsRef.current = positions }, [positions])
  useEffect(() => { limitOrdersRef.current = limitOrders }, [limitOrders])
  useEffect(() => { dataSourceRef.current = dataSource }, [dataSource])
  useEffect(() => { hlPairRef.current = hlPair }, [hlPair])
  useEffect(() => { currentTokenRef.current = currentToken }, [currentToken])

  // ─── Broadcast positions + limit orders for WalletHoldings sync ───
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('solcheddar-trading-positions', {
      detail: { positions, currentToken, dataSource }
    }))
  }, [positions, currentToken, dataSource])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('solcheddar-trading-limit-orders', {
      detail: { limitOrders, currentToken, dataSource }
    }))
  }, [limitOrders, currentToken, dataSource])

  // Market slippage → Ultra (see jupiter.js setMarketSlippageBps).
  useEffect(() => { setMarketSlippageBps(defaults.slippageBps) }, [defaults.slippageBps])

  // --- Pre-warm (Solana only) ---
  useEffect(() => {
    if (dataSource !== 'solana') return
    if (publicKey && currentToken) {
      startPreWarm(currentToken, defaults.buyAmountSol, defaults.sellPercentage, publicKey)
    } else { stopPreWarm() }
  }, [publicKey, currentToken, defaults.buyAmountSol, defaults.sellPercentage, dataSource])
  useEffect(() => () => stopPreWarm(), [])

  // --- Position persistence (Solana) ---
  // positionStore is the truth; `positions` is only the armed token's VIEW of
  // it. Writes for any other token land in that token's list and never touch
  // this view (chart 2's bot, TOKEN_SWITCH exits).
  const loadPositions = useCallback((token) => {
    setPositions(positionStore.get(token))
  }, [])

  const savePositions = useCallback((token, newPositions) => {
    positionStore.set(token, newPositions)
  }, [])

  useEffect(() => positionStore.subscribe((t) => {
    if (dataSourceRef.current === 'hyperliquid') return   // futures view comes from the HL poll
    // '*' = active wallet changed (positions-20261017)
    if (t === currentTokenRef.current || t === '*') setPositions(positionStore.get(currentTokenRef.current))
  }), [])

  // positions-20261017: size comes from the shared balance poll and entry
  // from the fill list (positionSync), so a known position is never averaged
  // here — two sockets and two charts used to double-count every buy. This
  // only creates the instant placeholder (or lets the tape price upgrade a
  // placeholder that has no fills yet), then asks for a balance read.
  const addPosition = useCallback((token, entryMcap, tokenAmount, tokenSupply, opts = {}) => {
    if (!token) return
    const source = opts.source || 'manual'
    const existing = positionStore.get(token)[0]
    const supply = tokenSupply > 0 ? tokenSupply : (Number(existing?.tokenSupply) || 0)
    const priceSol = supply > 0 && entryMcap > 0 ? entryMcap / supply : 0
    if (existing) {
      if (existing.source === 'instant' && (source === 'tape' || source === 'refined')
          && !(Array.isArray(existing.fills) && existing.fills.length) && priceSol > 0) {
        savePositions(token, [{
          ...existing,
          entryMcap,
          entryPriceSol: priceSol,
          tokenAmount,
          tokenSupply: supply || existing.tokenSupply,
          source,
          upgradedAt: Date.now(),
        }])
      }
    } else if (priceSol > 0) {
      savePositions(token, [{
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        token, entryMcap, entryPriceSol: priceSol, tokenAmount, tokenSupply: supply,
        entrySolPrice: solPriceRef.current,
        timestamp: Date.now(),
        source,
        fills: [], backfilledAt: 0, zeroReads: 0,
        pendingUntil: Date.now() + 90_000,   // may read 0 until the buy lands
      }])
    }
    positionSync.want(token)
    positionSync.refreshSoon(1500)
  }, [savePositions, solPriceRef])

  const removePosition = useCallback((token, id) => {
    savePositions(token, positionStore.get(token).filter(p => p.id !== id))
  }, [savePositions])

  // --- Wallet activity from server ---
  // positions-20261017: a wallet_activity message is a FILL for the entry
  // (deduped by signature — every socket delivers its own copy) plus a
  // trigger for the shared balance poll. It never changes size by itself.
  useEffect(() => {
    const handler = (e) => positionSync.onWalletActivity(e.detail)
    window.addEventListener('solcheddar-wallet-activity', handler)
    return () => window.removeEventListener('solcheddar-wallet-activity', handler)
  }, [])

  // --- Shared position sync (positions-20261017) ---
  useEffect(() => {
    positionSync.configure({
      getSupply: (mint) => (mint === currentTokenRef.current && tokenSupplyRef?.current > 0 ? tokenSupplyRef.current : 0),
      getFallbackPriceSol: (mint) => {
        const s = tokenSupplyRef?.current, m = lastBucketMcapRef?.current
        return (mint === currentTokenRef.current && s > 0 && m > 0) ? m / s : 0
      },
      onBalance: (mint, bal) => { if (mint === currentTokenRef.current) setTokenBalance(bal) },
    })
  }, [tokenSupplyRef, lastBucketMcapRef])
  useEffect(() => { positionSync.setWallet(publicKey || null) }, [publicKey])
  useEffect(() => {
    if (dataSource === 'solana' && currentToken) positionSync.want(currentToken)
  }, [dataSource, currentToken])

  // --- Limit order management ---
  const addLimitOrder = useCallback((order) => setLimitOrders(prev => [...prev, order]), [])
  const removeLimitOrder = useCallback((id) => setLimitOrders(prev => prev.filter(o => o.id !== id)), [])

  // ─── Solana limit place / cancel, venue-aware (orders-venue-20261016) ───
  // Returns the venue-specific fields to spread into the limit-order record.
  // `venue` defaults to the Order Defaults setting; moves pass the order's own.
  const placeSolLimit = useCallback(async ({ side, amount, pct, target, rawAmount, options = {}, venue }) => {
    const v = venue || venueOf(defaults)
    if (v === 'meteora') {
      const r = await placeMeteoraLimit({
        mint: currentToken, side, solAmount: amount, pct, rawAmount,
        targetMcapSol: target, owner: publicKey, signTransaction,
      })
      return { id: r.orderId, orderId: r.orderId, venue: 'meteora', pool: r.pool, binIds: [r.binId],
               targetMcap: r.targetMcap, rawAmount: r.rawAmount, signature: r.signature }
    }
    const r = side === 'buy'
      ? await executeLimitBuy(currentToken, amount, target, publicKey, signTransaction, options)
      : await executeLimitSell(currentToken, pct, target, publicKey, signTransaction, { ...options, ...(rawAmount ? { rawAmount } : {}) })
    return { id: r.orderId || `lo-${Date.now()}`, orderId: r.orderId, venue: 'jupiter',
             targetMcap: target, rawAmount: r.rawAmount || null, signature: r.signature }
  }, [defaults, currentToken, publicKey, signTransaction])

  const cancelSolLimit = useCallback(async (lo) => {
    if (lo.kind === 'stop') return               // client-side stop: nothing on-chain
    if (lo.venue === 'meteora') {
      return cancelMeteoraLimit({ pool: lo.pool, orderId: lo.orderId, binIds: lo.binIds, owner: publicKey, signTransaction })
    }
    return cancelTriggerOrder(lo.orderId, publicKey, signTransaction)
  }, [publicKey, signTransaction])

  const handleCancelLimitOrder = useCallback(async (lo) => {
    if (lo.futuresOrder && dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current
      if (!ws) return
      try {
        showToast('Cancelling order...', 'sell')
        const result = await hlRequest(ws, 'hl_cancel_order', { symbol: lo.symbol || lo.token, orderId: lo.orderId })
        if (result.code === 0) { removeLimitOrder(lo.id); showToast('Cancelled', 'sell'); if (pollRef.current) setTimeout(pollRef.current, 500) }
        else showToast(`Cancel failed: ${result.msg}`, 'sell')
      } catch (err) { showToast(`Cancel failed: ${err.message}`, 'sell') }
      return
    }
    if (lo.kind === 'stop') { removeLimitOrder(lo.id); showToast('Stop removed', 'sell'); return }
    if (!publicKey || !signTransaction) return
    try {
      const filled = lo.status === 'filled'
      showToast(filled ? 'Withdrawing filled Meteora order...' : `Cancelling limit ${lo.side}...`, 'sell')
      await cancelSolLimit(lo)
      removeLimitOrder(lo.id)
      showToast(filled ? 'Meteora fill withdrawn to wallet' : `Limit ${lo.side} cancelled`, 'sell')
    } catch (err) { showToast(`Cancel failed: ${err.message}`, 'sell') }
  }, [publicKey, signTransaction, removeLimitOrder, showToast, wsRef, cancelSolLimit])

  const handleMoveLimitOrder = useCallback(async (lo, newPrice) => {
    // ─── HYPERLIQUID ───
    if (lo.futuresOrder && dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current
      if (!ws || ws.readyState !== WebSocket.OPEN) { showToast('WebSocket not connected', 'sell'); return }
      // Optimistic: move tag immediately
      setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, targetMcap: newPrice } : o))
      try {
        showToast('Moving order...', 'sell')
        await hlRequest(ws, 'hl_cancel_order', { symbol: lo.symbol || lo.token, orderId: lo.orderId })
        removeLimitOrder(lo.id)
        const price = parseFloat(newPrice.toPrecision(5))
        const result = await hlRequest(ws, 'hl_place_order', {
          symbol: lo.symbol || lo.token,
          side: lo.side === 'buy' ? 'BUY' : 'SELL',
          orderType: 'LIMIT',
          qty: String(lo.amount),
          price: String(price),
          tif: 'Gtc',
        })
        if (result.code === 0) { showToast(`Order moved to $${price.toFixed(2)}`, 'buy'); if (pollRef.current) setTimeout(pollRef.current, 500) }
        else showToast(`Move failed: ${result.msg}`, 'sell')
      } catch (err) {
        showToast(`Move failed: ${err.message}`, 'sell')
        // Revert optimistic update on failure
        setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, targetMcap: lo.targetMcap } : o))
      }
      return
    }

    if (lo.venue === 'meteora' && (lo.status === 'filled' || lo.status === 'partial')) {
      showToast('This Meteora order has fills — Cancel it to withdraw, then place a new one', 'sell')
      return
    }

    // ─── SOLANA client-side stop — just move the level ───
    if (lo.kind === 'stop') {
      setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, targetMcap: newPrice } : o))
      showToast('Stop moved', 'sell')
      return
    }

    // ─── SOLANA (Jupiter / Meteora) — cancel, re-place, restore on failure ───
    // Funds are locked in the old order, so it must be cancelled first. If
    // the new placement then fails, the ORIGINAL is re-placed at its old
    // price/size so the order is never silently lost.
    if (!publicKey || !signTransaction) { showToast('Unlock wallet first', 'sell'); return }
    setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, targetMcap: newPrice } : o))
    const args = { side: lo.side, amount: lo.amount, pct: lo.percentage, rawAmount: lo.rawAmount, venue: lo.venue || 'jupiter' }
    try {
      showToast('Moving order...', 'sell')
      await cancelSolLimit(lo)
    } catch (err) {
      showToast(`Move failed (old order still live): ${err.message}`, 'sell')
      setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, targetMcap: lo.targetMcap } : o))
      return
    }
    removeLimitOrder(lo.id)
    try {
      const r = await placeSolLimit({ ...args, target: newPrice })
      addLimitOrder({ ...lo, ...r, status: undefined, timestamp: Date.now() })
      showToast('Order moved', 'buy')
    } catch (err) {
      console.warn('[Limit] move re-place failed, restoring original:', err)
      try {
        const r = await placeSolLimit({ ...args, target: lo.targetMcap })
        addLimitOrder({ ...lo, ...r, status: undefined, timestamp: Date.now() })
        showToast(`Move failed (${err.message}) — original order restored`, 'sell')
      } catch (err2) {
        showToast(`ORDER LOST: move failed and restore failed (${err2.message}). Was ${lo.side} @ ${formatMcap(lo.targetMcap)}`, 'sell')
      }
    }
  }, [publicKey, signTransaction, currentToken, removeLimitOrder, addLimitOrder, showToast, wsRef, cancelSolLimit, placeSolLimit])
  // ═══════════════════════════════════════════════════════════════════
  // HYPERLIQUID: Poll positions + orders + balance (single combined call)
  // ═══════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (dataSource !== 'hyperliquid' || !hlPair) return
    const getWs = () => {
      const raw = wsRef?.current
      if (!raw) return null
      const ws = raw?.readyState !== undefined ? raw : raw?.current
      return (ws && ws.readyState === WebSocket.OPEN) ? ws : null
    }
    // positions-20261017: after 3 failed polls in a row, clear the futures
    // tags instead of leaving the last (possibly closed) positions up.
    let fails = 0
    const failed = () => {
      if (++fails >= 3) setPositions(prev => (prev.some(p => p.futuresPosition) ? prev.filter(p => !p.futuresPosition) : prev))
    }
    const poll = async () => {
      const ws = getWs(); if (!ws) { failed(); return }
      try {
        const result = await hlRequest(ws, 'hl_poll', {})
        // Positions
        const pos = result.positions
        if (!(pos?.code === 0 && Array.isArray(pos.data))) failed()
        if (pos?.code === 0 && Array.isArray(pos.data)) {
          fails = 0
          setPositions(pos.data.map(p => ({
            id: p.symbol || p.positionId, token: p.symbol,
            entryMcap: parseFloat(p.avgOpenPrice), tokenAmount: parseFloat(p.qty),
            entrySolPrice: 0, timestamp: Date.now(),
            positionId: p.positionId || p.symbol, side: p.side,
            leverage: p.leverage, unrealizedPNL: p.unrealizedPNL,
            margin: p.margin, liqPrice: p.liqPrice,
            marginMode: p.marginMode, avgOpenPrice: p.avgOpenPrice,
            futuresPosition: true,
          })))
          // Broadcast positions for WalletHoldings
          window.dispatchEvent(new CustomEvent('solcheddar-hl-positions', {
            detail: pos.data.map(p => ({
              id: p.symbol, symbol: p.symbol,
              side: p.side, qty: parseFloat(p.qty),
              avgOpenPrice: parseFloat(p.avgOpenPrice),
              unrealizedPNL: parseFloat(p.unrealizedPNL || 0),
              leverage: p.leverage, liqPrice: p.liqPrice,
            }))
          }))
        }
        // Orders
        const ord = result.orders
        if (ord?.code === 0 && Array.isArray(ord.data)) {
          setLimitOrders(ord.data.map(o => ({
            id: o.orderId, orderId: o.orderId,
            side: o.side === 'BUY' ? 'buy' : 'sell',
            targetMcap: parseFloat(o.price), amount: o.qty,
            symbol: o.symbol, token: o.symbol,
            timestamp: o.ctime, orderType: o.orderType, futuresOrder: true,
          })))
          // Broadcast open orders for WalletHoldings
          window.dispatchEvent(new CustomEvent('solcheddar-hl-orders', {
            detail: ord.data.map(o => ({
              id: o.orderId, orderId: o.orderId,
              side: o.side === 'BUY' ? 'buy' : 'sell',
              price: parseFloat(o.price), qty: o.qty,
              symbol: o.symbol, orderType: o.orderType,
            }))
          }))
        }
        // Account / balance
        const acc = result.account
        if (acc?.code === 0 && acc.data) {
          const acct = Array.isArray(acc.data) ? acc.data[0] : acc.data
          window.dispatchEvent(new CustomEvent('solcheddar-hl-balance', {
            detail: {
              available: acct.available || '0', margin: acct.margin || '0',
              unrealizedPnl: acct.crossUnrealizedPNL || '0',
              positionMode: acct.positionMode || 'ONE_WAY',
            }
          }))
        }
      } catch (err) { console.warn('[HL] Poll error:', err.message); failed() }
    }
    pollRef.current = poll
    const initial = setTimeout(poll, 2000)
    const interval = setInterval(poll, 10000)
    return () => { clearTimeout(initial); clearInterval(interval) }
  }, [dataSource, hlPair, wsRef])

  // --- Trigger immediate poll (called after order actions) ---
  const pollNow = useCallback(() => {
    if (pollRef.current) setTimeout(() => pollRef.current(), 500)
  }, [])

  // --- Direct limit order placement (bypasses modal, uses defaults) ---
  const placeLimitDirect = useCallback(async (side, price) => {
    // No price (LMT-mode buy/sell buttons) → place at current price/mcap.
    // Axis clicks always pass an explicit row price.
    if (price == null) price = lastBucketMcapRef?.current
    if (!(price > 0) || !isFinite(price)) { showToast('No price yet', 'sell'); return }
    // ─── HYPERLIQUID ───
    if (dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current; const pair = hlPairRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) { showToast('WebSocket not connected', 'sell'); return }
      if (!pair) { showToast('No futures pair selected', 'sell'); return }
      if (/^STK(-OPT)?:/i.test(String(pair?.base || ''))) { showToast('Stocks/options are view-only (no trading)', 'sell'); return }   // schwab-20261103
      const isBuy = side === 'buy'
      const roundedPrice = parseFloat(price.toPrecision(5))

      // Detect current position to know whether this limit is closing/reducing
      const curPos = positionsRef.current.find(p =>
        p.futuresPosition && (p.token === pair.base || p.symbol === pair.base)
      )
      const posSide = curPos ? (String(curPos.side || '').toUpperCase() === 'LONG' ? 'long' : 'short') : null
      const isClosing = !!curPos && (
        (posSide === 'long' && !isBuy) ||
        (posSide === 'short' && isBuy)
      )

      // Sizing — same rules as market orders.
      const leverage = defaults.futuresLeverage || 10
      let qty
      if (isClosing) {
        const pct = Math.max(1, Math.min(100, Number(defaults.sellPercentage) || 100))
        const posQty = Math.abs(parseFloat(curPos.tokenAmount) || parseFloat(curPos.qty) || 0)
        if (posQty <= 0) { showToast('Position size unknown', 'sell'); return }
        if (pct >= 100) {
          qty = String(parseFloat(posQty.toPrecision(6)))
        } else {
          if (pair.szDecimals == null) { showToast('Pair metadata still loading — try again in a second', 'sell'); throw new Error('szDecimals not loaded') }
          const szDecL = pair.szDecimals
          qty = String(Math.floor(posQty * (pct / 100) * 10 ** szDecL) / 10 ** szDecL)
          if (!(parseFloat(qty) > 0)) { showToast('Reduce rounds to 0 — position too small for that %', 'sell'); throw new Error('Reduce rounds to zero') }
        }
      } else {
        const marginUsd = parseFloat(defaults.futuresQty) || 0
        if (marginUsd <= 0) { showToast('Set margin first', 'sell'); return }
        // For limit orders, size against the LIMIT PRICE (not mark) so margin actually
        // corresponds to the order's eventual fill cost.
        const notionalUsd = marginUsd * leverage
        const rawQty = notionalUsd / roundedPrice
        const szDec = (pair.szDecimals != null && Number.isFinite(pair.szDecimals)) ? pair.szDecimals : 4
        qty = String(parseFloat(rawQty.toFixed(szDec)))
        // Exchange rejects zero sizes and sizes with more decimals than the
        // asset's szDecimals. A synthesized pair (picked before metadata
        // loaded) has no szDecimals — sizing blind risks the reject, so say
        // so instead of sending garbage; App upgrades the pair in place the
        // moment the real list lands, and the retry then succeeds.
        if (!(parseFloat(qty) > 0)) { showToast('Size rounds to 0 — raise margin or leverage', 'sell'); throw new Error('Qty rounds to zero') }
        if (pair.szDecimals == null) { showToast('Pair metadata still loading — try again in a second', 'sell'); throw new Error('szDecimals not loaded') }
      }

      try {
        await ensureLeverage(ws, pair.base, leverage)
        const actionLabel = isClosing
          ? (defaults.sellPercentage >= 100 ? 'Limit close' : `Limit reduce ${defaults.sellPercentage}%`)
          : (isBuy ? 'Limit long' : 'Limit short')
        showToast(`${actionLabel} ${qty} ${pair.base} @ $${roundedPrice.toFixed(2)}...`, side)
        const result = await hlRequest(ws, 'hl_place_order', {
          symbol: pair.base, side: isBuy ? 'BUY' : 'SELL',
          orderType: 'LIMIT', qty, price: String(roundedPrice), tif: 'Gtc',
        })
        if (result.code !== 0) throw new Error(result.msg || 'Order rejected')
        showToast(`${actionLabel} placed!`, 'buy')
        // Optimistic tag
        addLimitOrder({
          id: result.data?.orderId || `lo-${Date.now()}`,
          orderId: result.data?.orderId || '',
          side, targetMcap: roundedPrice, amount: qty,
          symbol: pair.base, token: pair.base,
          timestamp: Date.now(), orderType: 'LIMIT', futuresOrder: true,
          isClosing, // future tag relabeling can read this
        })
        pollNow()
      } catch (err) { showToast(`Failed: ${err.message}`, 'sell') }
      return
    }

    // ─── SOLANA — direct placement, sized from defaults ───
    // (Formerly fell back to the modal "for amount input" — but the amounts
    // already live in defaults: buyAmountSol from the quantity wheel and
    // sellPercentage, exactly how the HL branch sizes itself. Mirrors the
    // modal path's executeLimitBuy/Sell calls and tag shapes.)
    if (!publicKey || !signTransaction) { showToast('Unlock wallet first', 'sell'); return }
    if (!currentToken) { showToast('Select a token first', 'sell'); return }
    const target = price

    try {
      if (side === 'buy') {
        const solAmount = parseFloat(defaults.buyAmountSol) || 0
        if (solAmount <= 0) { showToast('Set buy amount first', 'sell'); return }
        showToast(`Limit buy ${solAmount} SOL @ ${target.toFixed(2)}...`, 'buy')
        const r = await placeSolLimit({ side: 'buy', amount: solAmount, target })
        addLimitOrder({
          ...r, side: 'buy', amount: solAmount,
          token: currentToken, timestamp: Date.now(),
        })
        showToast('Limit buy placed!', 'buy')
      } else {
        const pct = Math.max(1, Math.min(100, Number(defaults.sellPercentage) || 100))
        showToast(`Limit sell ${pct}% @ ${target.toFixed(2)}...`, 'sell')
        const r = await placeSolLimit({ side: 'sell', pct, target })
        addLimitOrder({
          ...r, side: 'sell', percentage: pct,
          token: currentToken, timestamp: Date.now(),
        })
        showToast('Limit sell placed!', 'sell')
      }
    } catch (err) {
      showToast(`Limit failed: ${err.message}`, 'sell')
    }
  }, [defaults, showToast, addLimitOrder, wsRef, pollNow,
      publicKey, signTransaction, currentToken, lastBucketMcapRef, placeSolLimit])

  // --- Listen for cancel requests from WalletHoldings ---
  useEffect(() => {
    if (dataSource !== 'hyperliquid') return
    const handler = async (e) => {
      const order = e.detail
      const ws = wsRef?.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      try {
        showToast('Cancelling order...', 'sell')
        const result = await hlRequest(ws, 'hl_cancel_order', { symbol: order.symbol, orderId: order.orderId })
        if (result.code === 0) {
          removeLimitOrder(order.orderId)
          showToast('Cancelled', 'sell')
        } else showToast(`Cancel failed: ${result.msg}`, 'sell')
      } catch (err) { showToast(`Cancel failed: ${err.message}`, 'sell') }
    }
    window.addEventListener('solcheddar-hl-cancel-order', handler)
    return () => window.removeEventListener('solcheddar-hl-cancel-order', handler)
  }, [dataSource, wsRef, removeLimitOrder, showToast])

  // --- Listen for Solana limit cancel requests from WalletHoldings ---
  useEffect(() => {
    if (dataSource !== 'solana') return
    const handler = (e) => {
      const lo = e.detail
      if (lo) handleCancelLimitOrder(lo)
    }
    window.addEventListener('solcheddar-cancel-limit-order', handler)
    return () => window.removeEventListener('solcheddar-cancel-limit-order', handler)
  }, [dataSource, handleCancelLimitOrder])

  // --- Listen for close position requests from WalletHoldings ---
  useEffect(() => {
    if (dataSource !== 'hyperliquid') return
    const handler = async (e) => {
      const pos = e.detail
      const ws = wsRef?.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      try {
        showToast(`Closing ${pos.side} ${pos.symbol}...`, 'sell')
        const result = await hlRequest(ws, 'hl_flash_close', { symbol: pos.symbol, qty: pos.qty })
        if (result.code === 0) showToast('Position closed!', 'buy')
        else showToast(`Close failed: ${result.msg}`, 'sell')
      } catch (err) { showToast(`Close failed: ${err.message}`, 'sell') }
    }
    window.addEventListener('solcheddar-hl-close-position', handler)
    return () => window.removeEventListener('solcheddar-hl-close-position', handler)
  }, [dataSource, wsRef, showToast])

  // --- Listen for fills history requests from useHlFills hook ---
  useEffect(() => {
    if (dataSource !== 'hyperliquid') return
    const handler = async (e) => {
      const ws = wsRef?.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      const limit = e.detail?.limit || 200
      try {
        const result = await hlRequest(ws, 'hl_fills', { limit })
        if (result.code === 0 && Array.isArray(result.data)) {
          window.dispatchEvent(new CustomEvent('solcheddar-hl-fills', { detail: result.data }))
        } else {
          window.dispatchEvent(new CustomEvent('solcheddar-hl-fills', { detail: [] }))
        }
      } catch (err) {
        console.warn('[HL] Fills fetch failed:', err.message)
        window.dispatchEvent(new CustomEvent('solcheddar-hl-fills', { detail: [] }))
      }
    }
    window.addEventListener('solcheddar-hl-fills-request', handler)
    return () => window.removeEventListener('solcheddar-hl-fills-request', handler)
  }, [dataSource, wsRef])

  // --- Poll Solana limit fills: Jupiter + Meteora (orders-venue-20261016) ---
  // Jupiter: an order leaving the active list is looked up in order HISTORY
  // so a fill is told apart from a cancel/expiry. Meteora: each order's
  // account is read; a filled order stays listed (status 'filled') until
  // Cancel/withdraw pulls the proceeds to the wallet. Poll errors are
  // counted and surfaced once after 3 in a row instead of being swallowed.
  const pollErrRef = useRef(0)
  const unlinkOco = useCallback((lo) => {
    if (lo.linkedOrderId) removeLimitOrder(lo.linkedOrderId)
  }, [removeLimitOrder])
  useEffect(() => {
    if (dataSource !== 'solana' || limitOrders.length === 0 || !publicKey) return
    let busy = false
    const pollInterval = setInterval(async () => {
      if (busy) return
      busy = true
      try {
        const all = limitOrdersRef.current
        const jup = all.filter(lo => lo.kind !== 'stop' && lo.venue !== 'meteora' && lo.orderId)
        const met = all.filter(lo => lo.venue === 'meteora' && lo.status !== 'filled')
        if (jup.length) {
          const openOrders = await getOpenTriggerOrders(publicKey)
          const openIds = new Set(openOrders.map(o => o.orderKey || o.publicKey))
          const gone = jup.filter(lo => !openIds.has(lo.orderId))
          if (gone.length) {
            let hist = []
            try { hist = await getTriggerOrderHistory(publicKey) } catch (e) { console.warn('[Limit] history lookup failed:', e.message) }
            for (const lo of gone) {
              const h = hist.find(o => (o.orderKey || o.publicKey) === lo.orderId)
              const st = h ? classifyTriggerOrder(h) : 'unknown'
              removeLimitOrder(lo.id)
              if (st === 'filled' || st === 'partial') {
                unlinkOco(lo)
                showToast(`Limit ${lo.side} ${st === 'partial' ? 'partly ' : ''}filled @ ${formatMcap(lo.targetMcap)}!`, lo.side)
              } else if (st === 'cancelled' || st === 'expired') {
                showToast(`Limit ${lo.side} @ ${formatMcap(lo.targetMcap)} ${st}`, 'sell')
              } else {
                showToast(`Limit ${lo.side} @ ${formatMcap(lo.targetMcap)} closed (status unknown — check wallet)`, 'sell')
              }
            }
          }
        }
        for (const lo of met) {
          const { status } = await getMeteoraLimitStatus({ pool: lo.pool, orderId: lo.orderId })
          if (status === 'filled') {
            setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, status: 'filled' } : o))
            unlinkOco(lo)
            showToast(`Meteora limit ${lo.side} filled @ ${formatMcap(lo.targetMcap)} — Cancel it to withdraw proceeds`, lo.side)
          } else if (status === 'partial' && lo.status !== 'partial') {
            setLimitOrders(prev => prev.map(o => o.id === lo.id ? { ...o, status: 'partial' } : o))
            showToast(`Meteora limit ${lo.side} partly filled`, lo.side)
          } else if (status === 'closed') {
            removeLimitOrder(lo.id)
            showToast(`Meteora limit ${lo.side} closed`, 'sell')
          }
        }
        pollErrRef.current = 0
      } catch (err) {
        if (++pollErrRef.current === 3) showToast(`Limit fill check failing: ${err.message}`, 'sell')
        console.warn('[Limit] fill poll error:', err?.message)
      } finally { busy = false }
    }, 5000)
    return () => clearInterval(pollInterval)
  }, [dataSource, limitOrders.length, publicKey, removeLimitOrder, showToast, unlinkOco])

  // --- Client-side stops (OCO SL leg) ---
  // Watched against the live chart mcap (current token only), 1s cadence.
  // Fires a market sell on the stop's venue, cancels the linked TP, then
  // removes itself either way (no machine-gunning a failing sell).
  useEffect(() => {
    if (dataSource !== 'solana' || !publicKey) return
    const firing = new Set()
    const t = setInterval(() => {
      if (isPaperActive()) return   // paperwallet-20261027: real stops wait (kept, not fired) while Paper is active
      const mcap = lastBucketMcapRef?.current
      if (!(mcap > 0)) return
      for (const lo of limitOrdersRef.current) {
        if (lo.kind !== 'stop' || lo.token !== currentTokenRef.current || firing.has(lo.id)) continue
        if (mcap > lo.targetMcap) continue
        firing.add(lo.id)
        removeLimitOrder(lo.id)
        showToast(`Stop hit @ ${formatMcap(mcap)} — selling ${lo.percentage}%`, 'sell')
        mktSell(lo.venue, lo.token, lo.percentage, publicKey, signTransaction, defaults.slippageBps)
          .then(r => showToast(`Stop sold! ${String(r?.txid || r).slice(0, 8)}...`, 'sell'))
          .catch(err => showToast(err.inflight ? 'Stop sell submitted — verifying…' : `Stop sell FAILED: ${err.message}`, 'sell'))
        const tp = limitOrdersRef.current.find(o => o.id === lo.linkedOrderId)
        if (tp) {
          removeLimitOrder(tp.id)
          cancelSolLimit(tp).catch(err => showToast(`TP cancel failed — cancel it manually: ${err.message}`, 'sell'))
        }
      }
    }, 1000)
    return () => clearInterval(t)
  }, [dataSource, publicKey, signTransaction, defaults.slippageBps, removeLimitOrder, showToast, cancelSolLimit, lastBucketMcapRef])

  // --- Load existing Meteora limit orders for this coin (venue = Meteora) ---
  // So orders placed earlier / in another tab show up with Cancel.
  useEffect(() => {
    if (dataSource !== 'solana' || !publicKey || !currentToken || venueOf(defaults) !== 'meteora') return
    let dead = false
    listMeteoraLimitOrders(currentToken, publicKey).then(list => {
      if (dead) return
      const have = new Set(limitOrdersRef.current.map(o => o.orderId))
      for (const o of list) {
        if (have.has(o.orderId)) continue
        addLimitOrder({ ...o, id: o.orderId, venue: 'meteora', token: currentToken, timestamp: Date.now() })
      }
    }).catch(err => dbg('[MeteoraLO] list skipped:', err?.message))
    return () => { dead = true }
  }, [dataSource, publicKey, currentToken, defaults.orderVenue, addLimitOrder])

  // --- Reconcile position with chain (Solana only) ---
  // positions-20261017: replaced by the shared balance poll in positionSync
  // (all mints, summed accounts, closes on two zero reads in a row).

  // --- Close position ---
  const handleClosePosition = useCallback(async (pos) => {
    // paperclose-20261027: in Paper, the X closes the PAPER position (100%, live
    // fill at the bid, no fee). A leftover tag from a paper buy (no chain fill) is cleared.
    // Real positions still can't be sold from Paper — nothing on-chain here.
    if (isPaperActive()) {
      const mint = pos?.token || currentToken
      const ghost = pos && pos.source === 'instant'
      if (!pos?.futuresPosition && paperPosition(mint)) {
        const mcap = mint === currentTokenRef.current ? paperFillMcap(rawTradesRef, lastBucketMcapRef, 'sell') : 0
        if (!(mcap > 0)) { showToast('PAPER: open that coin on the chart to close it', 'sell'); return }
        try {
          const r = paperSell(mint, 100, mcap)
          showToast(`PAPER closed · ${r.pnl >= 0 ? '+' : ''}${r.pnl.toFixed(4)} SOL`, r.pnl >= 0 ? 'buy' : 'sell')
          if (ghost && pos.id != null) removePosition(mint, pos.id)
        } catch (err) { showToast(`PAPER: ${err.message}`, 'sell') }
        return
      }
      if (ghost && pos.id != null) { removePosition(mint, pos.id); return }
      showToast('Paper wallet active — switch to a real wallet to close this', 'sell'); return
    }
    if (pos.futuresPosition && dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current; if (!ws) return
      try {
        showToast(`Closing ${pos.side} position...`, 'sell')
        const result = await hlRequest(ws, 'hl_flash_close', { symbol: pos.token, qty: pos.tokenAmount })
        if (result.code === 0) { showToast('Position closed!', 'buy'); setPositions(prev => prev.filter(p => p.id !== pos.id)) }
        else showToast(`Close failed: ${result.msg}`, 'sell')
      } catch (err) { showToast(`Close failed: ${err.message}`, 'sell') }
      return
    }
    if (!publicKey || !signTransaction) { showToast('Connect wallet first', 'sell'); return }
      const mint = pos.token || currentToken
    try {
      showToast('Closing position...', 'sell')
      // positions-20261017: the tag's OWN token (pos.token), never the armed chart's.
      const result = await mktSell(venueOf(defaults), mint, 100, publicKey, signTransaction, defaults.slippageBps)
      const txid = typeof result === 'string' ? result : result.txid
      showToast(`Closed! ${txid.slice(0, 8)}...`, 'sell')
      // The tag goes when the balance poll reads 0 twice (now + shortly after).
      window.dispatchEvent(new CustomEvent('solcheddar-position-closed', { detail: { mint } }))
      positionSync.want(mint)
      positionSync.refreshNow()
      setTimeout(() => positionSync.refreshNow(), 4000)
    } catch (err) {
      if (err.message === 'No tokens to sell') {
        removePosition(mint, pos.id)
        window.dispatchEvent(new CustomEvent('solcheddar-position-closed', { detail: { mint } }))
        positionSync.refreshSoon(2000)
      }
      else if (err.inflight) {
        // Close submitted but unverified — it may have landed. Don't show a
        // false failure or remove the position; the 15s zero-balance poller
        // reconciles it if the tokens are actually gone.
        showToast('Close submitted — verifying on-chain…', 'sell')
      }
      else showToast(`Close failed: ${err.message}`, 'sell')
    }
  }, [publicKey, signTransaction, currentToken, removePosition, showToast, wsRef, defaults, lastBucketMcapRef, rawTradesRef])

  // --- External position close listener ---
  useEffect(() => {
    // positions-20261017: a close elsewhere only triggers a balance read; the
    // poll removes the tag once the summed balance reads 0 twice.
    const handler = (e) => { if (e?.detail?.mint) { positionSync.want(e.detail.mint); positionSync.refreshSoon(1000) } }
    window.addEventListener('solcheddar-position-closed', handler)
    return () => window.removeEventListener('solcheddar-position-closed', handler)
  }, [currentToken, savePositions])

  // --- Detect existing balance (Solana only) ---
  // positions-20261017: the balance poll + chain backfill own detection now
  // (entry from history, current mcap with '@' only when none is found).
  const detectExistingPosition = useCallback(async (token) => {
    if (!token) return
    positionSync.want(token)
    positionSync.refreshSoon(300)
  }, [])

  // ═══════════════════════════════════════════════════════════════════
  // Market order execution — branched
  // ═══════════════════════════════════════════════════════════════════

  const executeMarketOrder = useCallback(async (side, opts = {}) => {
    // sellFraction: 0–1. When provided on a closing order, overrides
    // defaults.sellPercentage so the bot can scale out partials without
    // mutating the user's default sell%.
    // buyAmountSol: explicit per-order SOL size. The OrderflowBot's legs
    // size themselves (pilot = starterSizeSol, load = loadSizeSol) — without
    // this override every bot buy silently used the user's default buy
    // amount and the pilot/load size distinction only existed on paper.
    // Solana branch only; HL opening size still comes from futures defaults.
    const { sellFraction, buyAmountSol } = opts
    const overridePct = (typeof sellFraction === 'number' && sellFraction > 0 && sellFraction <= 1)
      ? sellFraction * 100
      : null
    const isPartial = overridePct != null && overridePct < 100

    // ─── PAPER (paperwallet-20261027) ───
    // Simulated long, no fees: buy at the last buy print, sell at the last sell print. Nothing signed or sent.
    if (isPaperActive()) {
      if (dataSourceRef.current === 'hyperliquid') {
        showToast('Paper wallet: futures orders are off', 'sell'); throw new Error(PAPER_BLOCK)
      }
      const tok = opts.token || currentToken
      const mcap = (tok && tok === currentToken) ? paperFillMcap(rawTradesRef, lastBucketMcapRef, side === 'buy' ? 'buy' : 'sell') : 0
      try {
        if (side === 'buy') {
          const sol = (typeof buyAmountSol === 'number' && buyAmountSol > 0) ? buyAmountSol : defaults.buyAmountSol
          paperBuy(tok, sol, mcap)
          showToast(`PAPER buy ${sol} SOL`, 'buy')
        } else {
          const pct = overridePct != null ? Math.max(0.1, Math.min(100, overridePct)) : (defaults.sellPercentage || 100)
          const r = paperSell(tok, pct, mcap)
          showToast(`PAPER sell ${pct.toFixed(pct % 1 === 0 ? 0 : 1)}% · ${r.pnl >= 0 ? '+' : ''}${r.pnl.toFixed(4)} SOL`, r.pnl >= 0 ? 'buy' : 'sell')
        }
      } catch (err) { showToast(`PAPER: ${err.message}`, 'sell'); throw err }
      return
    }

    // ─── HYPERLIQUID ───
    if (dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current; const pair = hlPairRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) { showToast('WebSocket not connected', 'sell'); throw new Error('WS not connected') }
      if (!pair) { showToast('No futures pair selected', 'sell'); throw new Error('No pair') }
      if (/^STK(-OPT)?:/i.test(String(pair?.base || ''))) { showToast('Stocks/options are view-only (no trading)', 'sell'); throw new Error('view-only') }   // schwab-20261103
      const isBuy = side === 'buy'

      // Detect current position on the active pair. HL is one-way mode so there's
      // at most one position per coin — if the clicked side opposes it, we're closing.
      const curPos = positionsRef.current.find(p =>
        p.futuresPosition && (p.token === pair.base || p.symbol === pair.base)
      )
      const posSide = curPos ? (String(curPos.side || '').toUpperCase() === 'LONG' ? 'long' : 'short') : null
      const isClosing = !!curPos && (
        (posSide === 'long' && !isBuy) ||
        (posSide === 'short' && isBuy)
      )

      // Sizing:
      //   Closing → sellPercentage of the actual coin position (qty in coin units).
      //   Opening/adding → futuresQty is MARGIN in USD; notional = margin × leverage;
      //                    coin qty = notional / markPrice.
      const leverage = defaults.futuresLeverage || 10
      let qty
      if (isClosing) {
        const defaultPct = Math.max(1, Math.min(100, Number(defaults.sellPercentage) || 100))
        const pct = overridePct != null ? Math.max(0.1, Math.min(100, overridePct)) : defaultPct
        const posQty = Math.abs(parseFloat(curPos.tokenAmount) || parseFloat(curPos.qty) || 0)
        if (posQty <= 0) { showToast('Position size unknown', 'sell'); throw new Error('No position qty') }
        // Flat (>=100%) passes the exchange's own posQty through — already
        // size-conformant, and it has always worked. REDUCE multiplies by a
        // percentage, which MINTS precision the asset may not allow (50% of
        // 0.0451 = 0.02255 -> 5dp on a 4dp asset -> "invalid size") — the
        // exact reason Reduce failed while Flat never did. Floor to
        // szDecimals, never round up: a reduce must not exceed its fraction.
        if (pct >= 100) {
          qty = String(parseFloat(posQty.toPrecision(6)))
        } else {
          if (pair.szDecimals == null) { showToast('Pair metadata still loading — try again in a second', 'sell'); throw new Error('szDecimals not loaded') }
          const szDecC = pair.szDecimals
          qty = String(Math.floor(posQty * (pct / 100) * 10 ** szDecC) / 10 ** szDecC)
          if (!(parseFloat(qty) > 0)) { showToast('Reduce rounds to 0 — position too small for that %', 'sell'); throw new Error('Reduce rounds to zero') }
        }
      } else {
        const marginUsd = parseFloat(defaults.futuresQty) || 0
        if (marginUsd <= 0) { showToast('Set margin first', 'sell'); throw new Error('No margin') }
        // For HL futures the chart Y-axis is the price directly (supply=1)
        const markPrice = lastBucketMcapRef?.current || 0
        if (markPrice <= 0) { showToast('Mark price unavailable', 'sell'); throw new Error('No mark price') }
        const notionalUsd = marginUsd * leverage
        const rawQty = notionalUsd / markPrice
        // Round to szDecimals from pair metadata, fallback to 4 decimals
        const szDec = (pair.szDecimals != null && Number.isFinite(pair.szDecimals)) ? pair.szDecimals : 4
        qty = String(parseFloat(rawQty.toFixed(szDec)))
        // Exchange rejects zero sizes and sizes with more decimals than the
        // asset's szDecimals. A synthesized pair (picked before metadata
        // loaded) has no szDecimals — sizing blind risks the reject, so say
        // so instead of sending garbage; App upgrades the pair in place the
        // moment the real list lands, and the retry then succeeds.
        if (!(parseFloat(qty) > 0)) { showToast('Size rounds to 0 — raise margin or leverage', 'sell'); throw new Error('Qty rounds to zero') }
        if (pair.szDecimals == null) { showToast('Pair metadata still loading — try again in a second', 'sell'); throw new Error('szDecimals not loaded') }
      }

      try {
        await ensureLeverage(ws, pair.base, leverage)
        const actionLabel = isClosing
          ? (isPartial
              ? `Trim ${overridePct.toFixed(0)}%`
              : (defaults.sellPercentage >= 100 ? 'Flat' : `Reduce ${defaults.sellPercentage}%`))
          : (isBuy ? 'Long' : 'Short')
        showToast(`${actionLabel} ${qty} ${pair.base}...`, isBuy ? 'buy' : 'sell')
        const orderParams = { symbol: pair.base, side: isBuy ? 'BUY' : 'SELL', orderType: 'MARKET', qty }
        // Safety: partials are reduce-only so they can never accidentally flip to a new position
        if (isClosing && isPartial) orderParams.reduceOnly = true
        const result = await hlRequest(ws, 'hl_place_order', orderParams)
        if (result.code !== 0) { console.error('[HL] Order rejected:', JSON.stringify(result)); throw new Error(result.msg || 'Order rejected') }
        showToast(`${actionLabel} filled!`, isBuy ? 'buy' : 'sell')
        // Surface the fill to the history list immediately from the order response
        const _provFill = hlFillFromStatuses(result.data?.statuses, {
          symbol: pair.base,
          side: isBuy ? 'buy' : 'sell',
          dir: isClosing ? (posSide === 'long' ? 'Close Long' : 'Close Short')
                         : (isBuy ? 'Open Long' : 'Open Short'),
        })
        if (_provFill) window.dispatchEvent(new CustomEvent('solcheddar-hl-fill', { detail: _provFill }))
        if (pollRef.current) setTimeout(pollRef.current, 500)
        return result.data?.orderId
      } catch (err) { console.error('HL market order failed:', err); showToast(`Failed: ${err.message}`, 'sell'); throw err }
    }
    // ─── SOLANA ───
    // opts.token: the order's OWN token. Bots pass it so an order never lands
    // on whatever the armed chart shows (chart 2's bot, a TOKEN_SWITCH exit
    // firing after the switch). Manual clicks omit it → armed token.
    const tok = opts.token || currentToken
    if (!publicKey) { showToast('Connect wallet first', 'sell'); return }
    if (!tok) { showToast('No token selected', 'sell'); return }
    try {
      let txid
      if (side === 'buy') {
        const amount = (typeof buyAmountSol === 'number' && buyAmountSol > 0)
          ? buyAmountSol
          : defaults.buyAmountSol
        showToast(`Market buy ${amount} SOL...`, 'buy')

        // Instant placeholder tag at current chart mcap (chart + wallet both use this path now).
        // Tape detection will replace this with the actual fill mcap when our trade hits the gRPC stream.
        // refineEntryFromTx is the backup if tape misses the trade.
        window.dispatchEvent(new CustomEvent('solcheddar-create-position', {
          detail: { token: tok, solAmount: amount, fromChart: true }
        }))

        let inflightBuy = false
        try {
          const result = await mktBuy(venueOf(defaults), tok, amount, publicKey, signTransaction, defaults.slippageBps, () => showToast('Buy sent! Confirming...', 'buy'))
          txid = result.txid
        } catch (err) {
          if (err.inflight) {
            // Submitted but unverified — the buy MAY have landed. Keep the
            // optimistic placeholder and let the balance reconciliation below
            // (the source of truth) decide. Do NOT treat as a failure.
            inflightBuy = true
            showToast('Buy submitted — verifying on-chain…', 'buy')
          } else {
            // Definitive failure (slippage, simulation, sign reject, revert).
            // Remove the optimistic placeholder so the UI doesn't lie. Only the
            // 'instant' source is removed — a pre-existing real position for
            // this token (DCA into existing) stays.
            const placeholder = positionStore.get(tok).find(p =>
              p.token === tok && p.source === 'instant'
            )
            if (placeholder) {
              savePositions(tok, positionStore.get(tok).filter(p => p.id !== placeholder.id))
            }
            throw err   // re-throw to outer catch for the failure toast
          }
        }
        if (!inflightBuy) {
          showToast(`Buy confirmed! ${txid.slice(0, 8)}...`, 'buy')

          // Notify WalletHoldings to refresh holdings list
          window.dispatchEvent(new CustomEvent('solcheddar-buy-executed', {
            detail: { token: tok, fromChart: true }
          }))
        }

        // positions-20261017: the Helius parse becomes a deduped fill, and the
        // shared balance poll (two zero reads to close) decides size/existence.
        const token = tok
        setTimeout(() => {
          refineEntryFromTx(txid, token)
          positionSync.want(token)
          positionSync.refreshNow()
        }, 4000)
      } else {
        const pct = overridePct != null ? Math.max(0.1, Math.min(100, overridePct)) : defaults.sellPercentage
        showToast(`${isPartial ? 'Trim' : 'Market sell'} ${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%...`, 'sell')
        let inflightSell = false
        try {
          const result = await mktSell(venueOf(defaults), tok, pct, publicKey, signTransaction, defaults.slippageBps, () => showToast('Sell sent! Confirming...', 'sell'))
          txid = typeof result === 'string' ? result : result.txid
          showToast(`Sell confirmed! ${txid.slice(0, 8)}...`, 'buy')
        } catch (err) {
          if (err.inflight) {
            // Submitted but unverified — the sell MAY have landed (tokens may
            // be gone). Fall through to the balance read (the source of truth).
            // Critically, do NOT trust pct on an unverified sell: only an
            // on-chain zero balance is allowed to clear the position.
            inflightSell = true
            showToast('Sell submitted — verifying on-chain…', 'sell')
          } else {
            throw err   // definitive failure → outer catch; position stays (sell didn't happen)
          }
        }

        // positions-20261017: the shared balance poll is the truth — size from
        // the summed balance, closed only on two zero reads in a row. Read now
        // and again shortly (the RPC can lag the fill).
        positionSync.want(tok)
        positionSync.refreshNow()
        setTimeout(() => positionSync.refreshNow(), 4000)
      }
    } catch (err) {
      console.error('Market order failed:', err)
      showToast(`Failed: ${err.message}`, 'sell')
      // trade-errlog-20261019: App posts this to the messages log so a
      // failed buy/sell (manual, armed or bot) can be read after the toast.
      try {
        const raw = String(err?.message || err || 'unknown error')
        const slip = /\b6001\b|0x1771|slippage/i.test(raw)
        window.dispatchEvent(new CustomEvent('solcheddar-trade-error', { detail: {
          side, token: tok,
          amount: side === 'buy'
            ? ((typeof buyAmountSol === 'number' && buyAmountSol > 0) ? buyAmountSol : defaults.buyAmountSol)
            : (overridePct != null ? overridePct : defaults.sellPercentage),
          slippageBps: defaults.slippageBps, venue: venueOf(defaults),
          reason: slip ? 'slippage exceeded' : raw,
          detail: slip ? raw : null,
          signature: err?.signature || null,
        } }))
      } catch {}
      throw err
    }
  }, [publicKey, signTransaction, currentToken, defaults, showToast, addPosition, savePositions, lastBucketMcapRef, tokenSupplyRef, wsRef])

  // ═══════════════════════════════════════════════════════════════════
  // Limit/OCO confirm — branched
  // ═══════════════════════════════════════════════════════════════════

  const handleOrderConfirm = useCallback(async (orderData) => {
    const { side, mcap: targetMcap } = orderModal
    setOrderModal({ show: false, side: 'buy', mcap: 0 })

    // ─── HYPERLIQUID ───
    if (dataSourceRef.current === 'hyperliquid') {
      const ws = wsRef?.current; const pair = hlPairRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) { showToast('WebSocket not connected', 'sell'); return }
      if (!pair) { showToast('No futures pair selected', 'sell'); return }
      if (/^STK(-OPT)?:/i.test(String(pair?.base || ''))) { showToast('Stocks/options are view-only (no trading)', 'sell'); return }   // schwab-20261103
      try {
        const price = String(targetMcap)
        const qty = String(orderData.amount || defaults.futuresQty || '0.01')
        const isBuy = side === 'buy'
        const leverage = defaults.futuresLeverage || 10
        await ensureLeverage(ws, pair.base, leverage)
        const isMarket = orderData.orderMode === 'market'
        showToast(`${isMarket ? 'Market' : 'Limit'} ${isBuy ? 'buy' : 'sell'} ${qty} ${pair.base}${isMarket ? '' : ' @ $' + price}...`, side)
        const orderParams = { symbol: pair.base, side: isBuy ? 'BUY' : 'SELL', orderType: isMarket ? 'MARKET' : 'LIMIT', qty }
        if (!isMarket) { orderParams.price = price; orderParams.tif = 'Gtc' }
        if (orderData.reduceOnly) orderParams.reduceOnly = true
        const result = await hlRequest(ws, 'hl_place_order', orderParams)
        if (result.code !== 0) throw new Error(result.msg || 'Order rejected')
        showToast(`${isMarket ? 'Market' : 'Limit'} ${isBuy ? 'buy' : 'sell'} placed!`, 'buy')
        if (pollRef.current) setTimeout(pollRef.current, 500)
      } catch (err) { console.error('HL order failed:', err); showToast(`Failed: ${err.message}`, 'sell') }
      return
    }

    // ─── SOLANA ───
    if (!publicKey) { showToast('Connect wallet first', 'sell'); return }
    if (!currentToken) { showToast('No token selected', 'sell'); return }
    try {
      const options = orderData.options || {}
      if (orderData.type === 'oco') {
        // TP = real limit sell (above price). SL = client-side stop watched
        // against the live chart mcap and fired as a market sell — a v1 sell
        // limit below price fills instantly, which is what the old SL did.
        showToast(`Placing OCO: TP @ ${formatMcap(orderData.tpMcap)} / SL @ ${formatMcap(orderData.slMcap)}...`, 'sell')
        const tp = await placeSolLimit({ side: 'sell', pct: orderData.tpPercentage, target: orderData.tpMcap, options })
        const slId = `stop-${Date.now()}`
        addLimitOrder({ ...tp, side: 'sell', percentage: orderData.tpPercentage, token: currentToken, timestamp: Date.now(), ocoType: 'tp', linkedOrderId: slId })
        addLimitOrder({ id: slId, orderId: null, kind: 'stop', venue: venueOf(defaults), side: 'sell', targetMcap: orderData.slMcap, percentage: orderData.slPercentage, token: currentToken, timestamp: Date.now(), ocoType: 'sl', linkedOrderId: tp.id })
        showToast('OCO placed: TP on-chain, SL watched in-app (keep tab open)', 'buy')
      } else if (side === 'buy') {
        showToast(`Placing limit buy ${orderData.amount} SOL @ ${formatMcap(targetMcap)}...`, 'buy')
        const r = await placeSolLimit({ side: 'buy', amount: orderData.amount, target: targetMcap, options })
        showToast(`Limit buy placed (${r.venue})! ${String(r.signature).slice(0, 8)}...`, 'buy')
        addLimitOrder({ ...r, side: 'buy', amount: orderData.amount, token: currentToken, timestamp: Date.now() })
      } else {
        showToast(`Placing limit sell ${orderData.percentage}% @ ${formatMcap(targetMcap)}...`, 'sell')
        const r = await placeSolLimit({ side: 'sell', pct: orderData.percentage, target: targetMcap, options })
        showToast(`Limit sell placed (${r.venue})! ${String(r.signature).slice(0, 8)}...`, 'sell')
        addLimitOrder({ ...r, side: 'sell', percentage: orderData.percentage, token: currentToken, timestamp: Date.now() })
      }
    } catch (err) { console.error('Limit order failed:', err); showToast(`Failed: ${err.message}`, 'sell') }
  }, [publicKey, signTransaction, currentToken, orderModal, showToast, addLimitOrder, removeLimitOrder, defaults, wsRef, placeSolLimit])

  // --- Fetch balance when modal opens for sell (Solana only) ---
  useEffect(() => {
    if (dataSource !== 'solana') return
    if (orderModal.show && orderModal.side === 'sell' && publicKey && currentToken) {
      getTokenBalance(publicKey, currentToken).then(b => setTokenBalance(b ?? 0)).catch(() => setTokenBalance(0))
    }
  }, [dataSource, orderModal.show, orderModal.side, publicKey, currentToken])

  // Memoized: this object is passed straight to TradingContext.Provider, so a
  // fresh literal per render re-rendered every consumer — including the
  // memoized FootprintChart, since context updates bypass React.memo. Deps are
  // the 5 state values plus the useCallback'd actions and currentToken.
  return useMemo(() => ({
    positions, limitOrders, tokenBalance, orderModal, setOrderModal,
    loadPositions, savePositions, addPosition, removePosition,
    executeMarketOrder, handleOrderConfirm, handleClosePosition,
    handleCancelLimitOrder, handleMoveLimitOrder, placeLimitDirect, pollNow, detectExistingPosition,
    // The charted (armed-chart) token this context executes against — exposed
    // so panels with their own event-fed token copy (WalletHoldings) can
    // detect drift instead of silently diverging from what a click will trade.
    currentToken,
  }), [
    positions, limitOrders, tokenBalance, orderModal, setOrderModal,
    loadPositions, savePositions, addPosition, removePosition,
    executeMarketOrder, handleOrderConfirm, handleClosePosition,
    handleCancelLimitOrder, handleMoveLimitOrder, placeLimitDirect, pollNow,
    detectExistingPosition, currentToken,
  ])
}